import { useEffect, useRef, useState } from 'react';

import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { DIFFERENCE_TIME_UNITS, getDifferenceToNow } from 'utils/dateTime';

import { TLoyaltyCooldown } from './types';

const INACTIVE_COOLDOWN: TLoyaltyCooldown = { isActive: false };

const getCooldownInfo = (
  incentiveType: LoyaltyPromotionType,
  cooldownAvailableAfter: string,
  enableRewardCooldown?: boolean
): TLoyaltyCooldown => {
  if (!cooldownAvailableAfter) {
    return INACTIVE_COOLDOWN;
  }

  if (incentiveType === LoyaltyPromotionType.REWARD && !enableRewardCooldown) {
    return INACTIVE_COOLDOWN;
  }

  if (
    getDifferenceToNow(DIFFERENCE_TIME_UNITS.SECONDS, cooldownAvailableAfter, {
      useStoreTime: true,
    }) > 0
  ) {
    return { isActive: true, endsAt: new Date(cooldownAvailableAfter) };
  }

  return INACTIVE_COOLDOWN;
};

export const useLoyaltyIncentiveCooldown = ({
  incentiveType,
}: {
  incentiveType: LoyaltyPromotionType;
}): TLoyaltyCooldown => {
  const { loyaltyUser } = useLoyaltyUser();
  const enableRewardCooldown = useFlag(LaunchDarklyFlag.ENABLE_REWARD_COOLDOWN);
  const cooldownAvailableAfter =
    loyaltyUser?.[
      incentiveType === LoyaltyPromotionType.OFFER
        ? 'offerRedemptionAvailability'
        : 'rewardRedemptionAvailability'
    ]?.availableAfter || '';

  const [cooldownInfo, setCooldownInfo] = useState(
    getCooldownInfo(incentiveType, cooldownAvailableAfter, enableRewardCooldown)
  );

  const pendingTimeoutRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    const newCooldownInfo = getCooldownInfo(
      incentiveType,
      cooldownAvailableAfter,
      enableRewardCooldown
    );

    const msToEndOfCooldown = newCooldownInfo.isActive
      ? getDifferenceToNow(DIFFERENCE_TIME_UNITS.MILLISECONDS, newCooldownInfo.endsAt)
      : 0;

    if (msToEndOfCooldown > 0) {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
      }

      pendingTimeoutRef.current = setTimeout(() => {
        setCooldownInfo(
          getCooldownInfo(incentiveType, cooldownAvailableAfter, enableRewardCooldown)
        );
      }, msToEndOfCooldown);
    }

    setCooldownInfo(newCooldownInfo);
  }, [cooldownAvailableAfter, incentiveType, enableRewardCooldown]);

  return cooldownInfo;
};
